import { ApiOperationItems } from "~enums/apiOperationItems";
import { useApiOperations } from "~hooks/useApiOperations.ts";
import type { SearchFilterParams } from "~pages/Conducts/ConductListPage/types/searchFilterParams.ts";
import type { UpsertConductData } from "~pages/Conducts/ConductUpsertModal/types/upsertConductData.ts";
import type { ConductResponse } from "~types/conduct.ts";
import { useCreateConduct } from "./useCreateConduct.ts";
import { useDeleteConduct } from "./useDeleteConduct.ts";
import { useUpdateConduct } from "./useUpdateConduct.ts";

export const useConductService = (params: SearchFilterParams) => {
  const { mutateAsync: createConductFn, isPending: isCreatePending } = useCreateConduct(params);
  const { mutateAsync: updateConductFn, isPending: isUpdatePending } = useUpdateConduct(params);
  const { mutateAsync: deleteConductFn, isPending: isDeletePending } = useDeleteConduct(params);

  const { create, update, remove } = useApiOperations(ApiOperationItems.CONDUCT);

  const upsertConduct = async (upsertModel: UpsertConductData): Promise<ConductResponse | undefined> => {
    if (upsertModel.conductId) {
      return update(() => updateConductFn(upsertModel));
    }

    return create(() => createConductFn(upsertModel));
  };

  const deleteConduct = async (conductId: string) => Boolean(await remove(() => deleteConductFn(conductId)));

  const isUpsertPending = isCreatePending || isUpdatePending;

  return {
    upsertConduct,
    isUpsertPending,
    deleteConduct,
    isDeletePending,
  };
};
